import React, { useState } from 'react';
import classNames from 'classnames';
import { Box, Button } from '@ktag/styleguide/dist/react';
import { i18n } from '@ktag/styleguide/dist/base';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import { useTranslation, Trans } from 'react-i18next';
import { ReactComponent as YourIcon } from '../../../assets/youricon.svg';
import Placeholder from './YourcomponentPlaceholder';
// local labels for when you're not using the translation api
// import labels from '../yourcomponent.18n';

// We expect proptype definitions including clearly
// marked required props. this will help developers understand
// and reuse your code
interface Props {
  id?: string;
  className?: string;
  additionalClassNames?: string;
}

// Use functional components
// We try to use functional components because classes are
// somewhat difficult for developers and machines alike. Also where possible
// try to separate your components into plain render components and components (potentially
// higher order components) with business logic.
const Yourcomponent = (props: Props) => {
  // How props work:
  // ID and additionalClassNames are generally expected props to offer
  // in a render component. This way, other consumers can reuse the component
  // and link to it by means of a hash or customize its looks by means of a custom
  // css class.
  const { id, className, additionalClassNames } = props;
  const [isClicked, setClicked] = useState(false);

  // How custom labels work
  // You can register any language or namespace with a map of translations
  // if you do not register a namespace using i18n.add, i18n will try to load
  // the labels via HTTP from the translation API.

  // local labels for when you're not using the translation api
  // i18n.add(labels.de.yourcomponent, 'de', 'yourcomponent');
  // i18n.add(labels.en.yourcomponent, 'en', 'yourcomponent');
  const { t } = useTranslation();

  // How tracking works:
  // Calling trackEvent on analytics  will track this event into any (possibly several)
  // trackers that are enabled for the current environment.
  const handleClick = () => {
    analytics.trackEvent(`${process.env.REACT_APP_NAME}`, 'Someone pressed Hello.');
    setClicked(true);
  };

  // How language switching works:
  // calling setCurrentLanguage to any available language string
  // will update all i18n based strings across the whole app. strings
  // that aren't found will fall back to the default ('de')
  const handleLanguageSwitch = () => {
    if (i18n.getCurrentLanguage() === 'de') {
      i18n.setLanguage('en');
    } else {
      i18n.setLanguage('de');
    }
  };

  // How to use icons from the styleguide's sprite
  // When passing a string identifier as 'icon' prop to a
  // component, many styleguide components will treat this as
  // a reference to a sprite from sprites.svg.
  const renderButtonWithSprite = () => (
    <Button
      label={t('hello')}
      icon="carrot"
      onClick={handleClick}
      additionalClassNames="yourcomponent__trackmebutton"
    />
  );

  // How to use your own icons
  // CRA comes with SVGR installed. You can simply load SVG files as
  // react components and then pass them as 'icon' prop. Many styleguide
  // components will understand this and output those icons as inline SVG.
  const renderButtonWithLocalSVG = () => (
    <Button
      label={i18n.getCurrentLanguage()}
      icon={YourIcon}
      onClick={handleLanguageSwitch}
      additionalClassNames="yourcomponent__languageswitch"
    />
  );

  // How translations are used
  // By calling t we can load strings from the currently
  // active language.
  // By calling the Trans component we can interpolate more
  // complex strings. This keeps complex strings in one piece instead of
  // speading them out in many variables.
  const renderBoxWithTranslations = () => (
    <Box type="positive" heading={t('world')}>
      <p>
        <Trans i18nKey="yourcomponent:thanksforclicking">
          <strong>{'goodwork'}</strong>
          {' '}
          and some more text
        </Trans>
      </p>
    </Box>
  );

  return (
    <div id={id} className={classNames(className, additionalClassNames)}>
      { renderButtonWithLocalSVG() }
      <h2 className="h2">{t('yourapp')}</h2>
      {
        isClicked
          ? renderBoxWithTranslations()
          : renderButtonWithSprite()
      }
    </div>
  );
};

// Use default prop definitions rather than initialising defaults or
// sanitizing empty values inside your component logic.
Yourcomponent.defaultProps = {
  id: '',
  className: 'yourcomponent',
  additionalClassNames: '',
};

export default Yourcomponent;
export {
  Placeholder,
};
